<template lang="pug">
portal(to="modals")
  Modal(:show="show" title="Users" class="gray-bg" @close="$emit('close', true)")
    template(v-slot:subheader)
      .searchArea
        FormInput(type="text", :placeholder="$t('common.search')", v-model="searchString", id="user-search", :outline="true").ml-a.mr-a
    .users-settings.p-a-4
      .d-f.m-b-2
        Button(@click="addNewUser()", style="white-space: nowrap", :class="`${!addUser ? 'very-blue' : 'very-blue-light'}`", v-if="[1, 6, 16].includes(permission)") {{ $t('settings.add_a_user') }}
        .ml-a
        .icon-buttons-group.m-r-2.m-l-2
          IconButton(icon="menu-left" :class="`${pagination.page > 1 ? 'blue' : 'gray'}`", :large-icon="true", @clicked="pagination.page > 1 ? paginate(-1) : null")
          IconButton(icon="menu-right" :class="`${pagination.page < pagination.maxPage ? 'blue' : 'gray'}`", :large-icon="true", @clicked="pagination.page < pagination.maxPage ? paginate(1) : null")
      CardMinimal
        .users(v-if="users.length > 0")
          ExpandableListItem(v-for="user in users", :key="user.id", :selected="selectedUser === user.id")
            .p-a-4(@click="selectUser(user.id)")
              p {{ user.email }}
              .description {{ getPermissionName(user.permission_id) }}
            template(slot="dots", v-if="[1, 6, 16].includes(permission)")
              ul
                li.red(@click="removeUser(user.id)")
                  i.mdi.mdi-trash-can-outline
                  | {{ $t('products.remove') }}
            template(slot="expand")
              .d-f.f-g.fd-r
                div
                  label ID
                  FormInput(readonly="readonly", :disabled="true" :id="`${user.id}-id`", :copy="true", :value="`${user.id}`")
                div
                  label Name
                  FormInput(readonly="readonly", :disabled="true" :id="`${user.id}-name`", :copy="true", :value="`${user.name}`")
        .p-a-4.no-search-users(v-else): p {{ $t('settings.no_users_matches_search') }}
      portal(to="modals")
        Modal(:show="addUser", title="Add a user" :stuck="createState !== ''" @close="cancelCreateUser()").auto-height
          .p-a-4
            p.m-b-1 {{ $t('settings.add_user_email').toUpperCase() }}
            FormInput(type="text", v-model="newUser.email", id="user-email", :outline="true").m-b-2
            p.m-b-1 {{ $t('settings.add_user_password').toUpperCase() }}
            FormInput(type="password", v-model="newUser.password", id="user-password", :outline="true").m-b-2
            p.m-b-1 {{ $t('settings.add_user_permission').toUpperCase() }}
            FormDropdown(:items="permissionList", :forceUp="true" :maxHeight="150" placeholder="Välj", :value="newUser.permission_id", @input="newUser.permission_id = $event.value").medium
            .d-f.m-t-2
              Button.m-r-4(@click="addUserValid ? createUser() : null", style="white-space: nowrap", :disabled="!addUserValid || createState !== ''", :class="`${addUserValid ? 'very-blue' : 'very-blue-light'}`")
                template(v-if="createState === ''") {{ $t('settings.add_new_user') }}
                template(v-else-if="createState === 'creating'") {{ $t('settings.adding_new_user') }}
                template(v-else-if="createState === 'created'") {{ $t('settings.new_user_added') }}
              Button.ml-a.outline(@click="cancelCreateUser()") {{ $t('common.cancel') }}
</template>

<script>

import ExpandableListItem from '@/components/Lists/ExpandableListItem.vue';

import { validateEmail } from '@/utils';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ExpandableListItem,
  },
  computed: {
    /**
     * Return the current list of users
     * @returns {?Array}
     */
    users() {
      const { pagination: { page, perPage }, searchString } = this;
      const users = this.$store.getters['AccountManager/users'];
      const start = (page - 1) * perPage;
      const list = searchString ? users.filter(user => searchString.toLowerCase().split(' ').every(word => user.email.toLowerCase().indexOf(word) > -1)) : users;
      // eslint-disable-next-line
      this.pagination.maxPage = ((list.length / perPage) % 1 === 0) ? (list.length / perPage) : Math.floor(list.length / perPage) + 1;
      return list.slice(start, start + perPage);
    },
    /**
     * Return the current list of permissions
     * @return {?Array}
     */
    permissions() {
      return this.$store.getters['AccountManager/permissions'];
    },
    /**
     * Return a readable list for the Dropdown component of permissions
     * @return {?Array}
     */
    permissionList() {
      const { permissions, newUser: { permission_id } } = this;
      if (!permissions) return [];
      return permissions.map(permission => ({
        string: permission.name,
        value: permission.id,
        description: this.t(`settings.permissions["${permission.description}"]`, permission.description),
        selected: permission.id === permission_id,
      }));
    },
    /**
     * Returns a boolean if the form for addUser is valid
     * @return {Boolean}
     */
    addUserValid() {
      const { email, password, permission_id } = this.newUser;
      return validateEmail(email) && password !== '' && permission_id !== '';
    },
  },
  methods: {
    /**
     * Paginate
     * @param {Number} num
     */
    paginate(num) {
      this.pagination.page += num;
    },
    /**
     * Dispatch event to get users
     */
    getUsers() {
      this.$store.dispatch('AccountManager/getUsers');
    },
    /**
     * Dispatch event to get permissions
     */
    getPermissions() {
      this.$store.dispatch('AccountManager/getPermissions');
    },
    /**
     * Get permission name based on ID
     * @param {Number} id
     * @returns {?String}
     */
    getPermissionName(id) {
      const { permissions } = this;
      const [permission] = permissions.filter(perm => perm.id === id);
      return permission ? permission.name : '';
    },
    /**
     * Select a user
     * @param {String} id
     */
    selectUser(id) {
      const { selectedUser } = this;
      this.selectedUser = selectedUser === id ? null : id;
      this.addUser = false;
    },
    /**
     * Toggle add new user
     */
    addNewUser() {
      this.selectedUser = null;
      this.addUser = true;
    },
    /**
     * Remove a user from this account
     */
    removeUser(id) {
      // eslint-disable-next-line
      swal({ title: this.$t('errors.are_you_sure'), text: this.$t('settings.once_user_removed'), icon: 'warning', buttons: true, dangerMode: true }).then(async (remove) => {
        if (remove) {
          const userRemoved = await this.$store.dispatch('AccountManager/removeUser', id);
          if (!userRemoved) {
            swal({ title: this.$t('errors.error_occured'), text: this.$t('settings.cant_remove_user'), icon: 'error' });
            return;
          }
          this.getUsers();
        }
      });
    },
    /**
     * Create a new user on this account
     */
    async createUser() {
      const { addUserValid, newUser, createState } = this;
      if (!addUserValid) return;

      if (createState !== '') {
        return;
      }

      this.createState = 'creating';

      const data = await this.$store.dispatch('AccountManager/addUser', newUser);
      if (data.error) {
        this.createState = '';
        swal({
          title: this.$t('errors.error_occured'),
          text: this.t(`errors["${data.message}"]`, this.$t('settings.cant_add_user')),
          icon: 'error',
        });
        return;
      }

      this.createState = 'created';
      this.newUser.email = '';
      this.newUser.password = '';

      setTimeout(() => {
        this.createState = '';
        this.addUser = false;
      }, 2000);

      this.getUsers();
    },
    /**
     * Cancel user creation
     */
    cancelCreateUser() {
      this.newUser = { email: '', password: '', permission_id: '' };
      this.addUser = false;
    },
    t(str, fallbackStr) {
      // eslint-disable-next-line
      return this.$t && this.$te ? this.$te(str) ? this.$t(str) : fallbackStr : fallbackStr || str;
    },
  },
  watch: {
    /**
     * Watching for assumed. If changed, reload users.
     */
    assumed() {
      this.getUsers();
    },
  },
  data() {
    return {
      pagination: {
        page: 1,
        perPage: 10,
        maxPage: 1,
      },
      searchString: '',
      selectedUser: '',
      createState: '',
      addUser: false,
      newUser: {
        email: '',
        password: '',
        permission_id: '',
      },
    };
  },
  async mounted() {
    await this.getPermissions();
    await this.getUsers();
  },
};
</script>

<style lang="scss" scoped>
.searchArea {
  background-color: white;
  border-radius: 10px 10px 0 0;
  border-bottom: 1px solid #E7E9EC;
}

.no-search-users {
  text-align: center;
}

.description {
  color: lighten(black, 30%);
  margin-top: 2px;
}
</style>
